if (!customElements.get("m-slideshow")) {
  class MSlideshow extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.autoplay = this.dataset.autoplay === "true";
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed);
      this.showArrows = this.dataset.enableArrows === "true";
      this.showDots = this.dataset.enableDots === "true";
      this.adaptHeight = this.dataset.slideHeight === "adapt";
      this.id = this.dataset.id;
      this.sliderContainer = this.querySelector(".swiper-container");
      this.paginationEl = this.querySelector(".swiper-pagination");
      this.progressDesktop = this.dataset.progressDesktop === "true";
      // Ensure progress class is present when enabled (robust to markup caching)
      if (this.progressDesktop && this.paginationEl) {
        this.paginationEl.classList.add("m-dot-progress");
      }

      this.lastVideo = null;
      this.lastActive = null;
      this.timeout = null;
      this.initSlider();
    }

    initSlider() {
      this.slider = new MinimogLibs.Swiper(this.sliderContainer, {
        init: false,
        autoplay: this.autoplay
          ? {
              delay: this.autoplaySpeed * 1000,
              disableOnInteraction: true,
            }
          : false,
        slidesPerView: 1,
        slidesPerGroup: 1,
        loop: true,
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        speed: 1000,
        navigation: this.showArrows
          ? {
              nextEl: this.querySelector(".m-slider-controls__button-next"),
              prevEl: this.querySelector(".m-slider-controls__button-prev"),
            }
          : false,
        pagination: this.showDots
          ? {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
              bulletClass: "m-dot",
              bulletActiveClass: "m-dot--active",
            }
          : false,
        breakpoints: {
          992: {
            threshold: 2,
          },
        },
        on: {
          init() {
            setTimeout(() => {
              window.dispatchEvent(new Event("resize"));
            }, 100);
          },
        },
      });

      this.lastSlide = null;
      this.currentSlide = null;
      this.slider && this.slider.on("init", this.handleChange.bind(this));
      this.slider && this.slider.on("slideChange", this.handleChange.bind(this));
      // Progress handlers
      if (this.slider && this.paginationEl && this.progressDesktop) {
        // Sync progress with autoplay lifecycle
        this.slider.on("autoplayStart", () => this.resumeProgress());
        this.slider.on("autoplayStop", () => this.pauseProgress());
        this.slider.on("slideChangeTransitionStart", () => this.restartProgress());
      }
      this.slider.init();

      // Desktop-only progress controls; keep mobile dots unchanged
      this.bindProgressListeners();
      // Initialize paused state depending on autoplay
      this.updateProgressState();

      if (Shopify.designMode) {
        document.addEventListener("shopify:block:select", (e) => this.onBlockSelect(e));
      }
    }

    handleChange(swiper) {
      const { activeIndex, slides } = swiper;
      const slideType = slides[activeIndex] && slides[activeIndex].dataset.slideType;
      this.lastActive && this.slider.slideToLoop(this.lastActive);
      if (slideType === "video_slide") {
        const video = slides[activeIndex] && slides[activeIndex].querySelector("video");

        if (video) {
          let playPromise = video.play();
          this.lastVideo && this.lastVideo.pause();
          if (playPromise !== undefined) {
            playPromise.then(() => {
              this.lastVideo = video;
            });
          }
        }
      } else {
        this.lastVideo && this.lastVideo.pause();
        this.lastVideo = null;
      }
    }

    // --- Progress control helpers (desktop only) ---
    isDesktop() {
      return window.matchMedia("(min-width: 768px)").matches;
    }

    bindProgressListeners() {
      if (!this.paginationEl || !this.progressDesktop) return;
      // Pause/resume on pointer hover
      this.addEventListener("mouseenter", () => this.pauseProgress());
      this.addEventListener("mouseleave", () => this.resumeProgress());
      // Pause when window/tab hidden
      document.addEventListener("visibilitychange", () => this.updateProgressState());
      // On resize/breakpoint change, re-evaluate
      window.addEventListener("resize", () => this.updateProgressState());
      // Clicking bullets or arrows should restart progress
      this.paginationEl.addEventListener("click", () => this.restartProgress(), { passive: true });
      const nextBtn = this.querySelector(".m-slider-controls__button-next");
      const prevBtn = this.querySelector(".m-slider-controls__button-prev");
      nextBtn && nextBtn.addEventListener("click", () => this.restartProgress(), { passive: true });
      prevBtn && prevBtn.addEventListener("click", () => this.restartProgress(), { passive: true });
    }

    updateProgressState() {
      if (!this.paginationEl || !this.progressDesktop) return;
      const inEditor = typeof Shopify !== "undefined" && Shopify.designMode === true;
      // In editor, allow the progress animation to play for preview even if autoplay is off
      const shouldPause = !this.isDesktop() || document.hidden || (!this.autoplay && !inEditor) || (this.slider && this.slider.autoplay && this.slider.autoplay.running === false && !inEditor);
      this.paginationEl.classList.toggle("is-paused", !!shouldPause);
    }

    pauseProgress() {
      if (!this.paginationEl || !this.progressDesktop) return;
      this.paginationEl.classList.add("is-paused");
    }

    resumeProgress() {
      if (!this.paginationEl || !this.progressDesktop) return;
      const inEditor = typeof Shopify !== "undefined" && Shopify.designMode === true;
      if (!this.isDesktop() || (document.hidden) || (!this.autoplay && !inEditor)) return;
      this.paginationEl.classList.remove("is-paused");
    }

    restartProgress() {
      // Force restart by toggling pause quickly
      if (!this.paginationEl || !this.progressDesktop) return;
      this.paginationEl.classList.remove("is-paused");
      // No direct control over ::after animation; rely on class change of active bullet on slide change
      // If autoplay is not running, keep paused
      this.updateProgressState();
    }

    onBlockSelect(ev) {
      const block = ev.target;
      const index = Number(block.dataset.slide);
      this.lastActive = index;
      if (this.slider) {
        this.slider.slideToLoop(index);
      }
    }
  }

  customElements.define("m-slideshow", MSlideshow);
}
